import axios from 'axios'
import { getItem, setItem, removeItem, getJSON } from '@/utils/safeStorage'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'
const N8N_BASE_URL = import.meta.env.VITE_N8N_URL || import.meta.env.VITE_N8N_BASE_URL || 'http://localhost:5678'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const n8nApi = axios.create({
  baseURL: N8N_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
})

export const azureConfig = {
  storageAccountName: import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME,
  containerName: import.meta.env.VITE_AZURE_STORAGE_CONTAINER_NAME,
  sasToken: import.meta.env.VITE_AZURE_STORAGE_SAS_TOKEN
}

api.interceptors.request.use(
  config => {
    const token = getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (!config.headers['x-company-id']) {
      const company = getJSON('currentCompany')
      if (company?.id) {
        config.headers['x-company-id'] = company.id
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    const isAuthEndpoint = /\/auth\/(login|refresh)/.test(originalRequest?.url || '')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      const refreshToken = getItem('refreshToken')
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          })

          const { token: newToken, refreshToken: newRefreshToken } = response.data.data
          setItem('token', newToken)
          setItem('refreshToken', newRefreshToken)

          originalRequest.headers.Authorization = `Bearer ${newToken}`

          return api(originalRequest)
        } catch (refreshError) {
          removeItem('token')
          removeItem('refreshToken')
          removeItem('user')
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }

      removeItem('token')
      removeItem('refreshToken')
      removeItem('user')
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export const authApi = {
  login: credentials => api.post('/auth/login', credentials),
  loginMicrosoft: credentials => api.post('/auth/login/microsoft', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  changePassword: data => api.put('/auth/change-password', data),
  forceChangePassword: data => api.put('/auth/change-password-first-login', data),
  refreshToken: refreshToken => api.post('/auth/refresh', { refreshToken })
}

export const invoicesApi = {
  getAll: params => api.get('/invoices', { params }),
  getById: id => api.get(`/invoices/${id}`),
  getStats: () => api.get('/invoices/stats'),
  checkDuplicate: data => api.post('/invoices/check-duplicate', data),
  create: data => api.post('/invoices', data),
  // La empresa destinataria la elige el proveedor en el modal: va explícita en
  // el header en lugar de la empresa activa guardada en localStorage.
  createWithoutPO: (data, companyId) =>
    api.post('/invoices/without-po', data, { headers: { 'x-company-id': companyId } }),
  getSuppliersForWithoutPO: companyId =>
    api.get('/invoices/without-po/suppliers', { headers: { 'x-company-id': companyId } }),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  approve: (id, notes) => api.post(`/invoices/${id}/approve`, { notes }),
  confirmService: (id, notes) => api.post(`/invoices/${id}/confirm-service`, { notes }),
  // Factura cargada sin OC: buscar la OC del proveedor de esa factura y agregársela.
  searchPurchaseOrderForInvoice: (id, docNum, companyId) =>
    api.get(`/invoices/${id}/purchase-order/${docNum}`, { headers: { 'x-company-id': companyId } }),
  assignPurchaseOrder: (id, data, companyId) =>
    api.post(`/invoices/${id}/assign-purchase-order`, data, { headers: { 'x-company-id': companyId } }),
  reject: (id, reason) => api.post(`/invoices/${id}/reject`, { reason }),
  delete: id => api.delete(`/invoices/${id}`),
  softDelete: id => api.patch(`/invoices/${id}/soft-delete`),
  getPurchaseOrders: supplierId => api.get(`/invoices/purchase-orders/${supplierId}`),
  // Usuarios del portal elegibles como validador del servicio.
  getServiceValidators: () => api.get('/invoices/validators'),
  searchPurchaseOrder: (supplierId, docNum, companyId = null) =>
    api.get(`/invoices/search-purchase-order/${supplierId}/${docNum}`, {
      headers: companyId ? { 'x-company-id': companyId } : {}
    }),
  syncToSAP: (id, postingDate = null) => api.post(`/invoices/${id}/sync-to-sap`, postingDate ? { postingDate } : {}),
  voidInSAP: (id, reason) => api.post(`/invoices/${id}/void-in-sap`, { reason }),
  updateOCRData: (id, data) => api.put(`/invoices/${id}/update-ocr-data`, data),
  getChangeHistory: id => api.get(`/invoices/${id}/change-history`),
  // Enlace de líneas factura ↔ OC: líneas frescas de SAP y guardado del nuevo enlace
  // ({ [id del ítem]: [LineNum, ...] }).
  getPurchaseOrderLines: id => api.get(`/invoices/${id}/purchase-order-lines`),
  updateLineLinks: (id, lineMatches) => api.put(`/invoices/${id}/line-links`, { lineMatches }),
  saveWithholdings: (id, data) => api.post(`/invoices/${id}/withholdings`, data)
}

export const filesApi = {
  upload: async file => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  delete: fileId => api.delete(`/files/${fileId}`)
}

export const companiesApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/companies', { params })
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/companies/${id}`)
    return response.data
  },
  getStats: async () => {
    const response = await api.get('/companies/stats')
    return response.data
  },
  // Con supplierId solo devuelve las empresas a las que está ligado ese proveedor
  getSupplierCompanies: async (supplierId = null) => {
    const response = await api.get('/companies/supplier-companies', {
      params: supplierId ? { supplierId } : {}
    })
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/companies', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.put(`/companies/${id}`, data)
    return response.data
  },
  delete: async (id) => {
    const response = await api.delete(`/companies/${id}`)
    return response.data
  },
  testSAPConnection: async (id) => {
    const response = await api.post(`/companies/${id}/test-sap-connection`)
    return response.data
  },
  getAuditLog: async (id, params = {}) => {
    const response = await api.get(`/companies/${id}/audit-log`, { params })
    return response.data
  },
  getCompaniesWithSuppliers: async () => {
    const response = await api.get('/companies/with-suppliers')
    return response.data
  },
  getCompaniesWithUsers: async () => {
    const response = await api.get('/companies/with-users')
    return response.data
  }
}

export const uploadWindowsApi = {
  getAll: () => api.get('/upload-windows'),
  check: () => api.get('/upload-windows/check'),
  create: data => api.post('/upload-windows', data),
  update: (id, data) => api.put(`/upload-windows/${id}`, data),
  delete: id => api.delete(`/upload-windows/${id}`)
}

export const uploadCutoffApi = {
  getActive: () => api.get('/upload-cutoff'),
  set: data => api.post('/upload-cutoff', data),
  delete: id => api.delete(`/upload-cutoff/${id}`),
  getHistory: () => api.get('/upload-cutoff/history')
}

export const usersApi = {
  getUsers: async (params = {}) => {
    const response = await api.get('/users', { params })
    return response.data
  },
  getUser: async (id) => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },
  createUser: async (userData) => {
    const response = await api.post('/users', userData)
    return response.data
  },
  updateUser: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData)
    return response.data
  },
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`)
    return response.data
  },
  getUsersBySupplier: async (supplierId, includeInactive = false) => {
    const response = await api.get(`/users/supplier/${supplierId}`, {
      params: { includeInactive }
    })
    return response.data
  },
  getAuditLog: async (id, limit = 50, offset = 0) => {
    const response = await api.get(`/users/${id}/audit-log`, {
      params: { limit, offset }
    })
    return response.data
  },
  resetPassword: async (id, newPassword, sendEmail = false) => {
    const response = await api.post(`/users/${id}/reset-password`, {
      newPassword,
      sendEmail
    })
    return response.data
  },
  getFormData: async () => {
    const response = await api.get('/users/metadata/form-data')
    return response.data
  }
}

export const rolesApi = {
  getRoles: async (params = {}) => {
    const response = await api.get('/roles', { params })
    return response.data
  },
  getRole: async (id) => {
    const response = await api.get(`/roles/${id}`)
    return response.data
  },
  // Catálogo de permisos agrupado por módulo (alimenta la matriz del gestor)
  getPermissionCatalog: async (scope = null) => {
    const response = await api.get('/roles/permissions', {
      params: scope ? { scope } : {}
    })
    return response.data
  },
  createRole: async (roleData) => {
    const response = await api.post('/roles', roleData)
    return response.data
  },
  updateRole: async (id, roleData) => {
    const response = await api.put(`/roles/${id}`, roleData)
    return response.data
  },
  cloneRole: async (id, data) => {
    const response = await api.post(`/roles/${id}/clone`, data)
    return response.data
  },
  deleteRole: async (id) => {
    const response = await api.delete(`/roles/${id}`)
    return response.data
  }
}

export const suppliersApi = {
  getStats: async () => {
    const response = await api.get('/suppliers/stats')
    return response.data
  },
  getSuppliers: async (params = {}) => {
    const response = await api.get('/suppliers', { params })
    return response.data
  },
  getSupplier: async (id) => {
    const response = await api.get(`/suppliers/${id}`)
    return response.data
  },
  createSupplier: async (supplierData) => {
    const response = await api.post('/suppliers', supplierData)
    return response.data
  },
  updateSupplier: async (id, supplierData) => {
    const response = await api.put(`/suppliers/${id}`, supplierData)
    return response.data
  },
  deleteSupplier: async (id) => {
    const response = await api.delete(`/suppliers/${id}`)
    return response.data
  },
  // portalOnly: false también trae a los que no tienen U_Portal en SAP (buscador manual).
  getSAPBusinessPartners: async (search = '', { portalOnly = true } = {}) => {
    const response = await api.get('/suppliers/sap/business-partners', {
      params: { search, ...(portalOnly ? {} : { portalOnly: false }) }
    })
    return response.data
  },
  syncFromSAP: async (search = '') => {
    const response = await api.post('/suppliers/sync-from-sap', {
      search
    })
    return response.data
  },
  getAuditLog: async (id, limit = 50) => {
    const response = await api.get(`/suppliers/${id}/audit-log`, {
      params: { limit }
    })
    return response.data
  }
}

export const paymentTermsApi = {
  getAll: async () => {
    const response = await api.get('/payment-terms')
    return response.data
  },
  syncFromSAP: async () => {
    const response = await api.post('/payment-terms/sync-from-sap')
    return response.data
  }
}

export const costCentersApi = {
  getAll: async () => {
    const response = await api.get('/cost-centers')
    return response.data
  },
  syncFromSAP: async () => {
    const response = await api.post('/cost-centers/sync-from-sap')
    return response.data
  }
}

export const paymentVouchersApi = {
  getByInvoiceId: async (invoiceId) => {
    const response = await api.get(`/payment-vouchers/by-invoice/${invoiceId}`)
    return response.data
  },
  downloadPdf: async (voucherId) => {
    const response = await api.get(`/payment-vouchers/${voucherId}/pdf`, {
      responseType: 'blob'
    })
    return response.data
  },
  requestResend: async (voucherId) => {
    const response = await api.post(`/payment-vouchers/${voucherId}/resend-email`)
    return response.data
  }
}

export const userCompaniesApi = {
  getUserCompanies: async () => {
    const response = await api.get('/user/companies')
    return response.data
  },
  getCurrentCompany: async () => {
    const response = await api.get('/user/companies/current')
    return response.data
  },
  setDefaultCompany: async (companyId) => {
    const response = await api.put('/user/companies/default', { companyId })
    return response.data
  }
}

// Mis subcontratos: el backend los lee del Portal de Compras con sus propias credenciales.
export const subcontractsApi = {
  getStatus: async () => {
    const response = await api.get('/subcontracts/status')
    return response.data
  },
  getAll: async (params = {}) => {
    const response = await api.get('/subcontracts', { params })
    return response.data
  },
  getById: async id => {
    const response = await api.get(`/subcontracts/${id}`)
    return response.data
  }
}

export default api
